import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Wand2, Save, FileDown, Trash2, Loader2, AlertTriangle, History, Pencil } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DEFAULT_START = "Via Giovanni Falcone 43, Bareggio";
const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export default function RimborsoKmPage() {
  const now = new Date();
  const [techs, setTechs] = useState([]);
  const [form, setForm] = useState({ technician_id: "", year: now.getFullYear(), month: now.getMonth() + 1, car_model: "", price_per_km: "", total_amount: "", start_address: DEFAULT_START });
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const loadHistory = () => api.get("/mileage-sheets").then((r) => setHistory(r.data || [])).catch(() => {});

  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => setTechs(r.data || []));
    loadHistory();
  }, []);

  const pickTech = (id) => set("technician_id", id);

  useEffect(() => {
    if (!form.technician_id) return;
    api.get("/mileage-sheets/defaults", { params: { technician_id: form.technician_id, year: form.year, month: form.month } })
      .then(({ data }) => setForm((f) => ({
        ...f, car_model: data.car_model || "", price_per_km: data.price_per_km ?? "", start_address: data.start_address || DEFAULT_START,
        total_amount: data.suggested_total ?? (f.fromIncentive ? "" : f.total_amount), fromIncentive: data.suggested_total != null,
      })))
      .catch(() => {});
  }, [form.technician_id, form.year, form.month]);

  const generate = async () => {
    if (!form.technician_id || !Number(form.price_per_km) || !Number(form.total_amount)) {
      toast.error("Seleziona tecnico, prezzo al km e totale da rimborsare");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/mileage-sheets/generate", { ...form, price_per_km: Number(form.price_per_km), total_amount: Number(form.total_amount), year: Number(form.year), month: Number(form.month) }, { timeout: 180000 });
      setSheet(data);
      (data.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Scheda compilata: ${data.rows.length} giorni, ${data.total_km} km`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mileage-sheets", { ...sheet, price_per_km: Number(sheet.price_per_km) });
      setSheet({ ...sheet, id: data.id });
      if (data.cedolino_sync === "closed") toast.warning("Scheda salvata, ma il cedolino del mese è chiuso: riaprilo per aggiornare il rimborso");
      else if (data.cedolino_sync === "incentivo") toast.success(`Scheda salvata · giustifica il premio produzione (${eur(data.total_amount)}) già presente in Cedolini come rimborso km`);
      else toast.success(`Scheda salvata · Cedolini aggiornati con ${eur(data.total_amount)} di rimborso chilometrico`);
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  const openSheet = async (id) => {
    const { data } = await api.get(`/mileage-sheets/${id}`);
    setSheet(data);
    setForm({ technician_id: data.technician_id, year: data.year, month: data.month, car_model: data.car_model, price_per_km: data.price_per_km, total_amount: data.total_amount, start_address: data.start_address });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeSheet = async (h) => {
    if (!window.confirm(`Eliminare la scheda di ${h.technician_name} (${MONTHS[h.month - 1]} ${h.year})? Il rimborso verrà tolto anche dai cedolini.`)) return;
    try {
      const { data } = await api.delete(`/mileage-sheets/${h.id}`);
      toast.success(data.cedolino_sync === "closed" ? "Scheda eliminata (cedolino chiuso, non modificato)" : "Scheda eliminata e cedolino aggiornato");
      if (sheet?.id === h.id) setSheet(null);
      loadHistory();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="mileage-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Rimborsi</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">Scheda Rimborso Chilometrico</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Scegli tecnico e mese, inserisci veicolo, €/km e il totale da rimborsare: la scheda viene compilata dal lunedì al venerdì
          in base agli impianti assegnati, con distanze stradali reali proporzionate al totale. Al salvataggio il rimborso viene aggiunto ai <b>Cedolini</b>.
        </p>
      </div>
      <MileageForm form={form} set={set} techs={techs} pickTech={pickTech} onGenerate={generate} busy={busy} />
      {sheet && <MileageTable sheet={sheet} setSheet={setSheet} onSave={save} busy={busy} />}
      <MileageHistory items={history} onOpen={openSheet} onDelete={removeSheet} />
    </div>
  );
}

function MileageForm({ form, set, techs, pickTech, onGenerate, busy }) {
  const years = [form.year - 1, Number(form.year), Number(form.year) + 1].map(Number);
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="mileage-form">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
        <div className="md:col-span-4">
          <Label>Tecnico</Label>
          <Select value={form.technician_id} onValueChange={pickTech}>
            <SelectTrigger className="rounded-sm" data-testid="mileage-tech"><SelectValue placeholder="Scegli tecnico…" /></SelectTrigger>
            <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Mese</Label>
          <Select value={String(form.month)} onValueChange={(v) => set("month", Number(v))}>
            <SelectTrigger className="rounded-sm" data-testid="mileage-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Anno</Label>
          <Select value={String(form.year)} onValueChange={(v) => set("year", Number(v))}>
            <SelectTrigger className="rounded-sm" data-testid="mileage-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[...new Set(years)].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-4">
          <Label>Modello auto</Label>
          <Input value={form.car_model} onChange={(e) => set("car_model", e.target.value)} placeholder="es. Fiat Panda 1.2" className="rounded-sm" data-testid="mileage-car" />
        </div>
        <div className="md:col-span-2">
          <Label>Rimborso €/km</Label>
          <Input type="number" step="0.0001" min="0" value={form.price_per_km} onChange={(e) => set("price_per_km", e.target.value)} placeholder="0.45" className="rounded-sm font-mono" data-testid="mileage-price" />
        </div>
        <div className="md:col-span-2">
          <Label>Totale da rimborsare €</Label>
          <Input type="number" step="0.01" min="0" value={form.total_amount} onChange={(e) => set("total_amount", e.target.value)} placeholder="500" className="rounded-sm font-mono" data-testid="mileage-total" />
        </div>
        <div className="md:col-span-6">
          <Label>Punto di partenza</Label>
          <Input value={form.start_address} onChange={(e) => set("start_address", e.target.value)} className="rounded-sm" data-testid="mileage-start" />
        </div>
        <div className="flex items-end md:col-span-2">
          <Button onClick={onGenerate} disabled={busy} className="w-full rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="mileage-generate">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />} Compila
          </Button>
        </div>
      </div>
      {Number(form.price_per_km) > 0 && Number(form.total_amount) > 0 && (
        <div className="mt-3 text-xs text-slate-500" data-testid="mileage-km-preview">
          Km da giustificare: <b className="font-mono text-slate-900">{(Number(form.total_amount) / Number(form.price_per_km)).toFixed(1)} km</b>
          {form.fromIncentive && <span className="ml-2 rounded-sm bg-blue-50 px-1.5 py-0.5 text-[#0052FF]" data-testid="mileage-from-incentive">totale preso dal premio produzione</span>}
        </div>
      )}
    </div>
  );
}

function MileageTable({ sheet, setSheet, onSave, busy }) {
  const price = Number(sheet.price_per_km) || 0;
  const totKm = sheet.rows.reduce((s, r) => s + Number(r.km || 0), 0);
  const totAmt = sheet.rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const diff = Math.abs(totAmt - Number(sheet.total_amount || 0)) > 0.009;
  const editRow = (i, upd) => setSheet({ ...sheet, rows: sheet.rows.map((r, j) => (j === i ? { ...r, ...upd } : r)) });
  const pdf = async () => {
    const r = await api.get(`/mileage-sheets/${sheet.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `rimborso_km_${sheet.technician_name}_${sheet.year}_${sheet.month}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="mileage-sheet">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Car className="h-4 w-4 text-[#0052FF]" />
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{sheet.technician_name} · {MONTHS[sheet.month - 1]} {sheet.year} · {sheet.car_model || "—"}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={onSave} disabled={busy} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="mileage-save"><Save className="mr-2 h-4 w-4" /> Salva e aggiorna cedolino</Button>
          {sheet.id && <Button variant="outline" onClick={pdf} className="rounded-sm" data-testid="mileage-pdf"><FileDown className="mr-2 h-4 w-4" /> PDF</Button>}
        </div>
      </div>
      {diff && (
        <div className="mb-3 flex items-center gap-2 rounded-sm bg-amber-50 p-2 text-xs text-amber-800" data-testid="mileage-diff-warning">
          <AlertTriangle className="h-4 w-4" /> Il totale della tabella ({eur(totAmt)}) è diverso dal totale richiesto ({eur(sheet.total_amount)}): al salvataggio vale il totale della tabella.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="py-2">Data</th><th>Giorno</th><th>Percorso</th><th className="text-center">Impianti</th><th className="w-28 text-right">Km</th><th className="w-28 text-right">Importo</th>
          </tr></thead>
          <tbody>
            {sheet.rows.map((r, i) => (
              <tr key={r.date} className="border-b border-slate-100" data-testid={`mileage-row-${i}`}>
                <td className="py-1.5 font-mono text-xs">{r.date.split("-").reverse().join("/")}</td>
                <td className="text-xs">{r.weekday}</td>
                <td><Input value={r.route} onChange={(e) => editRow(i, { route: e.target.value })} className="h-8 rounded-sm text-xs" data-testid={`mileage-route-${i}`} /></td>
                <td className="text-center text-xs">{r.n_impianti || "—"}</td>
                <td className="pl-2"><Input type="number" step="0.1" value={r.km} onChange={(e) => editRow(i, { km: Number(e.target.value), amount: Math.round(Number(e.target.value) * price * 100) / 100 })} className="h-8 rounded-sm text-right font-mono text-xs" data-testid={`mileage-km-${i}`} /></td>
                <td className="text-right font-mono text-xs">{eur(r.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="font-bold">
            <td colSpan={4} className="py-2 text-right text-xs uppercase tracking-wider text-slate-500">Totale</td>
            <td className="text-right font-mono" data-testid="mileage-total-km">{totKm.toFixed(1)} km</td>
            <td className="text-right font-mono text-[#0052FF]" data-testid="mileage-total-amount">{eur(totAmt)}</td>
          </tr></tfoot>
        </table>
      </div>
    </div>
  );
}

function MileageHistory({ items, onOpen, onDelete }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="mileage-history">
      <div className="mb-3 flex items-center gap-2"><History className="h-4 w-4 text-slate-500" /><div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Storico schede</div></div>
      {items.length === 0 ? (
        <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">Nessuna scheda salvata</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((h) => (
            <div key={h.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`mileage-history-${h.id}`}>
              <div className="min-w-[180px] flex-1 font-semibold text-slate-900">{h.technician_name}</div>
              <div className="w-36 text-slate-600">{MONTHS[h.month - 1]} {h.year}</div>
              <div className="w-32 text-slate-500">{h.car_model || "—"}</div>
              <div className="w-24 text-right font-mono">{Number(h.total_km).toFixed(1)} km</div>
              <div className="w-28 text-right font-mono font-bold text-[#0052FF]">{eur(h.total_amount)}</div>
              <Button variant="ghost" size="icon" onClick={() => onOpen(h.id)} title="Apri" data-testid={`mileage-open-${h.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => onDelete(h)} title="Elimina" data-testid={`mileage-delete-${h.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
